import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { logger } from '@/features/help/utils/logger';
import { signerApi } from '../api/signer.api';
import { Signer } from '../types/signers';

interface UseSubmissionCompleteProps {
  userShortId: string | undefined;
  recordId: string | undefined;
  mappingShortId: string | undefined;
  signerShortId: string | undefined;
}

interface UseSubmissionCompleteReturn {
  allSigners: Signer[];
  isLoading: boolean;
  signersSubmitted: boolean;
  setSignersSubmitted: (value: boolean) => void;
  showInviteButton: boolean;
}

// Purpose: Custom hook to manage signer data fetching, button visibility logic, and navigation prevention for submission complete page
export const useSubmissionComplete = ({
  userShortId,
  recordId,
  mappingShortId,
  signerShortId,
}: UseSubmissionCompleteProps): UseSubmissionCompleteReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const [allSigners, setAllSigners] = useState<Signer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [signersSubmitted, setSignersSubmitted] = useState(false);
  const [showInviteButton, setShowInviteButton] = useState(true);

  // Navigation prevention logic
  useEffect(() => {
    logger.info('SubmissionComplete', 'Setting up navigation prevention', {
      url: window.location.href,
      pathname: window.location.pathname,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      userId: userShortId || 'unknown',
    });

    // Replace the current history entry to prevent going back
    window.history.replaceState(null, '', location.pathname);

    // Add a new history entry to ensure forward navigation works
    window.history.pushState(null, '', location.pathname);

    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      logger.debug('SubmissionComplete', 'Prevented navigation back to form', {
        state: event.state,
        timestamp: new Date().toISOString(),
      });

      // Push another state to maintain the submission complete page
      window.history.pushState(null, '', location.pathname);

      // Force replace the history to ensure we can't go back
      navigate(
        `/submission-complete/${userShortId}/${mappingShortId}/${recordId}/${signerShortId}`,
        { replace: true }
      );
    };

    window.addEventListener('popstate', handlePopState);

    logger.debug('SubmissionComplete', 'Added popstate listener to prevent form navigation');

    // Form data persistence has been removed - no localStorage cleanup needed
    logger.debug('SubmissionComplete', 'Form data persistence has been disabled');

    return () => {
      window.removeEventListener('popstate', handlePopState);
      logger.debug('SubmissionComplete', 'Removed popstate listener');
    };
  }, [navigate, location.pathname, userShortId, mappingShortId, recordId, signerShortId]);

  // Fetch signer data on component mount to determine button visibility
  useEffect(() => {
    const fetchSignerData = async () => {
      if (!userShortId || !recordId || !mappingShortId || !signerShortId) {
        setIsLoading(false);
        return;
      }

      try {
        logger.info('SubmissionComplete', 'Fetching signer data to determine button visibility', {
          userShortId,
          recordId,
          mappingShortId,
          signerShortId,
        });

        const fetchedSigners = await signerApi.getSigners(userShortId, mappingShortId, recordId);
        setAllSigners(fetchedSigners);

        // Find the current signer by signerShortId
        const currentSigner = fetchedSigners.find((signer) => signer.short_id === signerShortId);

        if (currentSigner) {
          logger.info('SubmissionComplete', 'Current signer found', {
            signerIndex: currentSigner.signer_index,
            signerEmail: currentSigner.email,
          });

          // If current signer is not the primary signer (index 1), hide the invite button
          if (currentSigner.signer_index > 1) {
            setShowInviteButton(false);
            logger.info('SubmissionComplete', 'Hiding invite button for non-primary signer');
          }
        } else {
          logger.warn('SubmissionComplete', 'Current signer not found in fetched signers', {
            signerShortId,
            fetchedSignersCount: fetchedSigners.length,
          });
        }
      } catch (error) {
        logger.error('SubmissionComplete', 'Failed to fetch signer data', { error });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSignerData();
  }, [userShortId, recordId, mappingShortId, signerShortId]);

  return {
    allSigners,
    isLoading,
    signersSubmitted,
    setSignersSubmitted,
    showInviteButton,
  };
};
